import React, { useEffect, useState, useCallback, useContext } from "react";
import { Button } from "react-bootstrap";
import api, { BASE_URL } from "../../Utils/api";
import FuncionariosModal from "./FuncionariosModal";
import FuncionariosImportarModal from "./FuncionariosImportarModal";
import ConfiguracoesCamposModal from "../Configuracoes/ConfiguracoesCampos/ConfiguracoesCamposModal";
import GridPadrao from "../GridPadrao/GridPadrao";
import PageHeader from "../PageHeader/PageHeader";
import { GlobalContext } from "../../Utils/GlobalContext";
import formatarData from "../../Utils/formatarData";
import '../../App.css';
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import { FaFileExcel, FaFileImport } from "react-icons/fa";
import { baixarModeloImportacaoFuncionarios } from "../../Utils/modeloImportacaoFuncionarios";


function Funcionarios() {
  const { toast, confirm } = useFeedback();
  const [funcionarios, setFuncionarios] = useState([]);
  const [selectedFuncionario, setSelectedFuncionario] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showConfigCamposModal, setShowConfigCamposModal] = useState(false);
  const [tccFuncionarioId, setTccFuncionarioId] = useState(null);
  const [baixandoModelo, setBaixandoModelo] = useState(false);
  const [showImportarModal, setShowImportarModal] = useState(false);

  const token = localStorage.getItem("authToken");
  const { globalData } = useContext(GlobalContext);
  const entidadeID = globalData?.entidade_id || localStorage.getItem("entidade_id");

  // Item #36 (LGPD): cpf/nascimento mascarados na exportação quando o LGPD da
  // entidade ativa está ligado. globalData.lgpdOcultarDados !== false trata
  // null/ausente (config ainda não carregou) como oculto — falha segura.
  const lgpdAtivo = globalData?.lgpdOcultarDados !== false;
  const exportMaskColumns = lgpdAtivo ? { cpf: "cpf", nascimento: "data_nascimento" } : {};

  // Resolve o TCC correspondente a Funcionários pelo nome/descrição.
  useEffect(() => {
    if (!entidadeID || !token) return;
    api
      .get(`${BASE_URL}/api/configuracaoCampos/ListarTiposConfiguracoesCampos/${entidadeID}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const lista = Array.isArray(res.data?.data) ? res.data.data : [];
        const match = lista.find((t) =>
          (t.nome || t.descricao || "").toLowerCase().includes("funcionari")
        );
        setTccFuncionarioId(match?.id ?? null);
      })
      .catch(() => setTccFuncionarioId(null));
  }, [entidadeID, token]);

  const handleOpenConfigCampos = () => {
    if (tccFuncionarioId != null) setShowConfigCamposModal(true);
  };

  const handleEdit = (funcionario) => {
    setSelectedFuncionario(funcionario);
    setShowModal(true);
  };

  const handleAdd = () => {
    setSelectedFuncionario(null);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedFuncionario(null);
    fetchFuncionarios();
  };

  const handleExcluir = async (funcionario) => {
    const ok = await confirm(`Tem certeza que deseja excluir o funcionário "${funcionario.nome}"?`);
    if (ok) {
      try {
        await api.delete(
          `${BASE_URL}/api/funcionarios/ExcluirFuncionario/${funcionario.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              id_entidade: entidadeID,
            },
          }
        );
        fetchFuncionarios();
      } catch (error) {
        console.error("Erro ao excluir funcionário:", error);
        toast.error(
          error.response?.data?.message ||
            "Erro ao excluir funcionário. Tente novamente."
        );
      }
    }
  };

  const handleDownloadModeloImportacao = async () => {
    if (!entidadeID) {
      toast.error("Entidade nao identificada para gerar o modelo.");
      return;
    }

    try {
      setBaixandoModelo(true);
      const response = await api.get(
        `${BASE_URL}/api/empresas/ListarEmpresasParaFuncionarios/${entidadeID}?incluirEntidade=1`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const empresas = response.data?.data || [];
      if (empresas.length === 0) {
        toast.warning("Cadastre ao menos uma empresa, filial ou fornecedor antes de preencher a planilha.");
      }
      baixarModeloImportacaoFuncionarios(empresas);
      toast.success("Modelo de importacao gerado.");
    } catch (error) {
      console.error("Erro ao gerar modelo de importacao de funcionarios:", error);
      toast.error(
        error.response?.data?.message ||
          "Erro ao gerar modelo de importacao. Tente novamente."
      );
    } finally {
      setBaixandoModelo(false);
    }
  };

  const columns = [
    { Header: "ID", accessor: "id" },
    { Header: "Nome", accessor: "nome" },
    {
      Header: "CPF",
      accessor: "cpf",
      Cell: ({ row }) => row.cpf || "—"
    },
    {
      Header: "Nascimento",
      accessor: "nascimento",
      Cell: ({ row }) => row.nascimento ? formatarData(row.nascimento) : "—"
    },
    {
      Header: "Data Contratação",
      accessor: "data_contratacao",
      Cell: ({ row }) => row.data_contratacao ? formatarData(row.data_contratacao) : "—"
    },
    {
      Header: "Data Demissão",
      accessor: "data_demissao",
      Cell: ({ row }) => row.data_demissao ? formatarData(row.data_demissao) : "—"
    },
    {
      Header: "Empresa",
      accessor: "empresa_nome",
      Cell: ({ row }) => row.empresa_nome || "—"
    },
    { Header: "Situação", accessor: "situacao" }
  ];

  const fetchFuncionarios = useCallback(() => {
    if (!entidadeID) return;

    return api
      .get(`${BASE_URL}/api/funcionarios/ListarFuncionarios/${entidadeID}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setFuncionarios(response.data.data || []);
      })
      .catch((error) => {
        console.error("Erro ao buscar funcionários:", error);
      });
  }, [entidadeID, token]);

  useEffect(() => {
    fetchFuncionarios();
  }, [fetchFuncionarios]);

  return (
    <div>
      <PageHeader titulo="Cadastro de Funcionários" path="/Funcionarios">
        <Button
          variant="outline-success"
          size="sm"
          className="d-flex align-items-center"
          data-tour="funcionarios-importar-via-xlsx"
          onClick={handleDownloadModeloImportacao}
          disabled={baixandoModelo}
          title="Baixar modelo de importacao de funcionarios"
        >
          <FaFileExcel className="me-1" />
          <span>{baixandoModelo ? "Gerando..." : "Importar Via XLSX"}</span>
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          className="d-flex align-items-center"
          data-tour="funcionarios-importar-xlsx"
          onClick={() => setShowImportarModal(true)}
          title="Importar funcionários a partir de uma planilha"
        >
          <FaFileImport className="me-1" />
          <span>Importar XLSX</span>
        </Button>
      </PageHeader>

      <GridPadrao
        columns={columns}
        data={funcionarios}
        onEdit={handleEdit}
        onAdd={handleAdd}
        addDataTour="funcionarios-adicionar"
        onInactivate={handleExcluir}
        exportMaskColumns={exportMaskColumns}
        onConfigCampos={tccFuncionarioId != null ? handleOpenConfigCampos : undefined}
      />

      <FuncionariosModal
        show={showModal}
        handleClose={handleClose}
        funcionario={selectedFuncionario}
        fetchFuncionarios={fetchFuncionarios}
      />

      <ConfiguracoesCamposModal
        show={showConfigCamposModal}
        handleClose={() => setShowConfigCamposModal(false)}
        configuracao={tccFuncionarioId != null ? { id: tccFuncionarioId } : null}
        fetchConfiguracoes={fetchFuncionarios}
      />

      <FuncionariosImportarModal
        show={showImportarModal}
        handleClose={() => setShowImportarModal(false)}
        fetchFuncionarios={fetchFuncionarios}
      />
    </div>
  );
}

export default Funcionarios;

